import React from 'react';
import {
  AccordionItem,
  Box,
  GroupedPlans,
  Hint,
  Inscription,
  OptionKey,
  PlanItem,
  Preloader,
  SearchInput,
  WarningLabel,
} from '@common-fe/common-fe';

import PlanTypeIcon from '@/modules/plan/PlanSetup/Sidebar/PlanTypeIcon';
import colors from '@/styles/colors';
import theme from '@/styles/theme';

import { usePlanTemplatePickerDropdown } from './hooks/usePlanTemplatePickerDropdown';
import DropdownTriggerInput from './DropdownTriggerInput';
import { AccordionItemWrapper, DropdownItem, DropdownPanel } from './PlanTemplatePickerDropdown.styles';

const HINT_WIDTH = 220;
const DEFAULT_WARNING_TEXT = 'Changing this plan will replace the current selection for all services below.';
const DEFAULT_EMPTY_MESSAGE = 'There are no plan templates.';
const NO_RESULTS_MESSAGE = "We haven't found any results.";

interface Props {
  testId?: string;
  options?: GroupedPlans[];
  defaultValues?: OptionKey[];
  onChange: (value: OptionKey[]) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  showError?: boolean;
  errorMessage?: string;
  placeholder?: string;
  width?: string;
  leftIcon?: React.ReactNode;
  showWarningBanner?: boolean;
  onClear?: () => void;
  columnsCount?: number;
}

const PlanTemplatePickerDropdown: React.FC<Props> = ({
  testId,
  options = [],
  defaultValues,
  onChange,
  isLoading,
  isDisabled,
  showError,
  errorMessage,
  placeholder,
  width,
  leftIcon,
  showWarningBanner,
  onClear,
  columnsCount,
}) => {
  const {
    ref,
    inputRef,
    isShown,
    setIsShown,
    openToLeft,
    searchValue,
    setSearchValue,
    filteredOptions,
    handlePick,
    handleOpen,
    selectedTemplateNames,
    selectedCategory,
    isItemSelected,
  } = usePlanTemplatePickerDropdown({ options, defaultValues, onChange });

  return (
    <Box ref={ref} style={{ position: 'relative' }} data-testid={`${testId}_grouped_dropdown`}>
      <Hint
        hideHint={!errorMessage || isShown}
        hintWidth={HINT_WIDTH}
        hintElement={
          <Box onFocus={handleOpen}>
            <DropdownTriggerInput
              isActive={isShown}
              setIsShown={setIsShown}
              isError={!!showError}
              isDisabled={isDisabled}
              placeholder={placeholder}
              width={width}
              leftIcon={leftIcon}
              onClear={onClear}
              columnsCount={columnsCount}
            >
              {selectedTemplateNames}
            </DropdownTriggerInput>
          </Box>
        }
      >
        <Inscription size="small" color="textOnColor" textAlign="center" weight={500}>
          {errorMessage}
        </Inscription>
      </Hint>

      {isShown && (
        <DropdownPanel $openToLeft={openToLeft} onMouseDown={(e) => e.stopPropagation()}>
          <Box
            pad={{ bottom: 'spacing12' }}
            border={{ color: 'border1', side: 'bottom' }}
            height={{ min: 'fit-content' }}
            style={{ flexShrink: 0 }}
          >
            <Box pad={{ horizontal: 'spacing16' }}>
              <SearchInput
                inputRef={inputRef}
                placeholder="Search"
                value={searchValue}
                onChange={setSearchValue}
              />
            </Box>
          </Box>

          {isLoading && (
            <Box margin={{ top: 'm' }}>
              <Preloader size={16} />
            </Box>
          )}

          {!isLoading && (
            <Box style={{ overflowY: 'auto', flex: 1 }} pad={{ bottom: 'spacing12' }}>
              {showWarningBanner && (
                <Box
                  margin={{ horizontal: 'spacing16', top: 'spacing12' }}
                  pad="spacing8"
                  background={{ color: 'warningContainer' }}
                  border={{ size: '1px', color: 'warningBorder' }}
                  round="fieldRound"
                  height={{ min: 'fit-content' }}
                >
                  <Inscription color="textBody" size="small" lineHeight="18px" weight={500}>
                    <WarningLabel>{DEFAULT_WARNING_TEXT}</WarningLabel>
                  </Inscription>
                </Box>
              )}

              {filteredOptions.length > 0 &&
                filteredOptions.map((item) => (
                  <AccordionItemWrapper
                    key={item.category}
                    pad={{ left: theme.spacings.spacing18 }}
                    margin={{ top: theme.spacings.spacing12 }}
                    height={{ min: '32px' }}
                  >
                    <AccordionItem
                      text={
                        <Box margin={{ left: theme.spacings.spacing12 }} direction="row" align="center">
                          {item.plans?.[0]?.type && (
                            <PlanTypeIcon
                              type={item.plans[0].type}
                              size="24px"
                              color={colors.cardTypes[item.plans[0].type as keyof typeof colors.cardTypes]}
                            />
                          )}
                          <Inscription color="textBody" weight="normal" style={{ marginLeft: '8px' }}>
                            {item.category} ({item.plans?.length || 0})
                          </Inscription>
                        </Box>
                      }
                      isExpandedDefault={selectedCategory === item.category}
                      isExpanded={!!searchValue || selectedCategory === item.category}
                      isLeftArrow
                    >
                      <Box margin={{ left: '36px', top: 'xs' }}>
                        {item.plans?.map((template: PlanItem) => (
                          <DropdownItem
                            type="button"
                            onClick={handlePick(template.id)}
                            active={isItemSelected(template.id)}
                            key={template.id}
                          >
                            <Inscription
                              size="medium"
                              lineHeight="20px"
                              weight={isItemSelected(template.id) ? 'bold' : 'normal'}
                            >
                              {template.name}
                            </Inscription>
                          </DropdownItem>
                        ))}
                      </Box>
                    </AccordionItem>
                  </AccordionItemWrapper>
                ))}

              {filteredOptions.length === 0 && (
                <Box pad={{ vertical: 'xs' }} height={{ min: 'fit-content' }} justify="center">
                  <Inscription size="small" textAlign="center" weight={500} color="textDisabled">
                    {searchValue ? NO_RESULTS_MESSAGE : DEFAULT_EMPTY_MESSAGE}
                  </Inscription>
                </Box>
              )}
            </Box>
          )}
        </DropdownPanel>
      )}
    </Box>
  );
};

export default React.memo(PlanTemplatePickerDropdown);
